import { Box, Center, Divider, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';

import ButtonedRadioGroup from '../../../components/ButtonedRadioGroup';
import EmployerNameAndPhoneInputs from '../../../components/EmployerNameAndPhoneInputs';
import { useLEAccordionItemContext } from '../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import TextWithTooltip from '../../../components/TextWithTooltip';
import TimeAtEmployment from '../../../components/TimeAtEmployment';
import { Label } from '../../../components/formComponents';
import Input from '../../../components/formComponents/Input';
import NumberInput from '../../../components/formComponents/NumberInput';
import PrimaryButton from '../../../components/ui/buttons/PrimaryButton';
import {
  EmployerPhoneLookupForm,
  EmploymentStatusEnum,
  useTemporaryInfoUpdateMutation,
} from '../../../gql/generated/graphql';
import { useTempInfo } from '../../../hooks';
import { useLookupRegistry } from '../../../hooks/useLookupRegistry';
import {
  EmploymentFormValues,
  employmentInitialValues,
  employmentValidationSchema,
} from '../utils';

const Employment = () => {
  const { info } = useTempInfo();
  const tempInfoData = info?.data;

  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();

  const { updateState, index, setOpenAccordionIndex } = useLEAccordionItemContext();
  const { registerLookup, finalizeLookups } = useLookupRegistry();

  const onSubmitForm = async (values: EmploymentFormValues) => {
    updateState({ status: 'loading' });

    const data = {
      employment_status: values.employment_status,
      employer_name: values.employer_name.slice(0, 25).trim(),
      employer_phone_number: values.employer_phone_number,
      job_title: values.job_title.slice(0, 20).trim(),
      years_at_job: values.years_at_job,
      months_at_job: values?.months_at_job ?? 0,
      salary: values.salary,
      pay_frequency: values.pay_frequency,
      previous_employer_name: values.previous_employer_name.slice(0, 25).trim(),
      previous_employer_phone_number: values.previous_employer_phone_number,
      previous_job_title: values.previous_job_title.slice(0, 20).trim(),
      previous_years_at_job: values.previous_years_at_job,
      previous_months_at_job: values?.previous_months_at_job ?? 0,
    };

    await updateTemporaryInfo({
      variables: {
        info: {
          id: info?.id,
          data,
        },
      },
    });

    updateState({ status: 'complete' });
    setOpenAccordionIndex(index + 1);
  };

  if (!tempInfoData) {
    return null;
  }

  return (
    <Box>
      <Center mb={{ base: 4, md: 0 }}>
        <TextWithTooltip label="Verifying your employment history gives lenders information about your regular income source(s) and helps them assess risk.">
          Why do we need this?
        </TextWithTooltip>
      </Center>
      <Formik<EmploymentFormValues>
        enableReinitialize
        initialValues={employmentInitialValues(tempInfoData)}
        validationSchema={employmentValidationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={onSubmitForm}
      >
        {({ isSubmitting, values, setFieldValue, errors, submitForm }) => {
          const hasErrors = Object.values(errors).some((value) => value !== '');
          const isEmployed = values?.employment_status === EmploymentStatusEnum.Employed;

          const handleNext = async () => {
            await finalizeLookups();
            submitForm();
          };

          return (
            <Form
              onKeyDown={(event) => {
                if (event.key === 'Enter' && (event.target as HTMLElement).tagName === 'INPUT') {
                  event.preventDefault();
                  handleNext();
                }
              }}
            >
              <Label mt={5}>EMPLOYMENT STATUS</Label>
              <ButtonedRadioGroup
                columns={3}
                collapseOnMobile={false}
                onChange={(value) => setFieldValue('employment_status', value)}
                options={[
                  { label: 'EMPLOYED', value: EmploymentStatusEnum.Employed },
                  { label: 'NOT EMPLOYED', value: EmploymentStatusEnum.NotEmployed },
                  { label: 'RETIRED', value: EmploymentStatusEnum.Retired },
                ]}
                value={values.employment_status}
                name="employment_status"
                defaultValue={EmploymentStatusEnum.Employed}
                mb={8}
              />
              {isEmployed && (
                <>
                  <EmployerNameAndPhoneInputs
                    form={EmployerPhoneLookupForm.BuyerCurrent}
                    nameField="employer_name"
                    phoneField="employer_phone_number"
                    mb={4}
                    registerLookup={registerLookup}
                  />
                  <Input label="JOB TITLE" name="job_title" mb={4} maxLength={50} />
                </>
              )}

              <NumberInput
                name="salary"
                isMoney
                type="decimal"
                _input={{ inputMode: 'numeric' }}
                label="ANNUAL GROSS INCOME"
                mb={2}
              />
              <TextWithTooltip label="Please provide your personal income information only. Include all sources of income such as employment earnings, self-employment income, investments, retirement funds, etc. This information should only reflect the income of the main applicant—do not include household or combined income.">
                Multiple sources of income?
              </TextWithTooltip>
              <Divider my="20px" />
              <TimeAtEmployment values={values} registerLookup={registerLookup} />

              <Center>
                <PrimaryButton
                  mt="30px"
                  mb={{
                    base: '20px',
                    lg: '0',
                  }}
                  loading={isSubmitting}
                  type="button"
                  onClick={handleNext}
                />
              </Center>
              {hasErrors && (
                <Text color="leaseEndRed" textAlign="center" mt={{ base: 0, lg: 4 }}>
                  Review fields above for errors.
                </Text>
              )}
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default Employment;
